import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { DateFilter } from '@/components/DateFilter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, TrendingUp, TrendingDown, DollarSign, Users, Package, BarChart3, PieChart, RefreshCw } from 'lucide-react';
import ShareViewButton from '@/components/common/ShareViewButton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart as RPieChart, Pie, Cell } from 'recharts';
import api from '@/lib/api';
import { toast } from 'sonner';

const COLORS = ['#3b82f6', '#f97316', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4', '#f59e0b', '#ec4899'];

export default function BIDashboardPage() {
  const [data, setData] = useState(null);
  const [dateFilter, setDateFilter] = useState({ start: null, end: null, period: "all" });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/analytics/overview?days=${period}`);
      setData(res.data);
    } catch { toast.error('Failed to load analytics'); } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, [period]);

  if (loading && !data) return <DashboardLayout><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin" /></div></DashboardLayout>;

  const s = data?.summary || {};

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="bi-dashboard">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-4">

              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-200/50">

                <BarChart3 size={24} className="text-white" />

              </div>

              <div>

                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">Business Intelligence</h1>
            <DateFilter onFilterChange={setDateFilter} />

                <p className="text-sm text-muted-foreground mt-0.5">Business intelligence dashboard</p>

              </div>

            </div>
            <p className="text-sm text-muted-foreground">Cross-module analytics and insights</p>
          </div>
          <div className="flex gap-2 items-center">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32 h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchData} className="rounded-xl"><RefreshCw size={14} /></Button>
            <ShareViewButton params={{ period }} defaultBookmarkName="BI Dashboard" />
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-cards">
          <KPICard icon={DollarSign} label="Net Profit" value={s.net_profit?.toLocaleString()} sub={`${s.profit_margin}% margin`} color={s.net_profit >= 0 ? 'text-success' : 'text-error'} />
          <KPICard icon={TrendingUp} label="Total Sales" value={s.total_sales?.toLocaleString()} sub={`${data?.period?.days || 30}d period`} color="text-info" />
          <KPICard icon={TrendingDown} label="Total Expenses" value={s.total_expenses?.toLocaleString()} sub={`incl. payroll`} color="text-warning" />
          <KPICard icon={Package} label="Inventory Value" value={s.inventory_value?.toLocaleString()} sub={`Retail: ${s.inventory_retail_value?.toLocaleString()}`} color="text-purple-600" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <MiniStat label="Monthly Payroll" value={s.total_payroll?.toLocaleString()} />
          <MiniStat label="Labor Cost" value={s.monthly_labor_cost?.toLocaleString()} badge={`${s.labor_cost_ratio}% of sales`} />
          <MiniStat label="Period" value={`${data?.period?.start} to ${data?.period?.end}`} />
        </div>

        <Tabs defaultValue="trend" className="space-y-4">
          <TabsList>
            <TabsTrigger value="trend">Revenue Trend</TabsTrigger>
            <TabsTrigger value="expenses">Expense Breakdown</TabsTrigger>
            <TabsTrigger value="departments">Dept. Costs</TabsTrigger>
            <TabsTrigger value="inventory">Inventory</TabsTrigger>
          </TabsList>

          <TabsContent value="trend">
            <Card>
              <CardHeader><CardTitle className="text-sm">Daily Revenue & Expenses</CardTitle></CardHeader>
              <CardContent>
                {data?.daily_trend?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={data.daily_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(5)} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="sales" stroke="#3b82f6" strokeWidth={2} name="Sales" dot={false} />
                      <Line type="monotone" dataKey="expenses" stroke="#f97316" strokeWidth={2} name="Expenses" dot={false} />
                      <Line type="monotone" dataKey="profit" stroke="#10b981" strokeWidth={2} name="Profit" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <div className="text-center py-12 text-sm text-muted-foreground">No data for this period</div>}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="expenses">
            <Card>
              <CardHeader><CardTitle className="text-sm">Expense Categories</CardTitle></CardHeader>
              <CardContent>
                {data?.expense_breakdown?.length > 0 ? (
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="w-full md:w-1/2">
                      <ResponsiveContainer width="100%" height={250}>
                        <RPieChart>
                          <Pie data={data.expense_breakdown} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                            {data.expense_breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <Tooltip />
                        </RPieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="w-full md:w-1/2 space-y-2">
                      {data.expense_breakdown.map((e, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-muted/50 border">
                          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full" style={{ background: COLORS[i % COLORS.length] }} /><span className="text-sm">{e.category}</span></div>
                          <span className="text-sm font-medium">{e.amount.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : <div className="text-center py-12 text-sm text-muted-foreground">No expense data</div>}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="departments">
            <Card>
              <CardHeader><CardTitle className="text-sm">Monthly Labor Cost by Department</CardTitle></CardHeader>
              <CardContent>
                {data?.department_costs?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data.department_costs}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                      <XAxis dataKey="department" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Bar dataKey="monthly_cost" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Monthly Cost" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="text-center py-12 text-sm text-muted-foreground">No department data</div>}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="inventory">
            <Card>
              <CardHeader><CardTitle className="text-sm">Inventory Value by Category</CardTitle></CardHeader>
              <CardContent>
                {data?.inventory_categories?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data.inventory_categories} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis dataKey="category" type="category" tick={{ fontSize: 10 }} width={100} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#06b6d4" radius={[0, 4, 4, 0]} name="Value" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="text-center py-12 text-sm text-muted-foreground">No inventory data</div>}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

function KPICard({ icon: Icon, label, value, sub, color }) {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center gap-2 mb-1"><Icon size={14} className={color || 'text-muted-foreground'} /><span className="text-xs text-muted-foreground">{label}</span></div>
        <div className={`text-lg font-bold font-outfit ${color || ''}`}>{value || '0'}</div>
        <div className="text-[10px] text-muted-foreground">{sub}</div>
      </CardContent>
    </Card>
  );
}

function MiniStat({ label, value, badge }) {
  return (
    <Card>
      <CardContent className="pt-3 pb-2">
        <div className="text-[10px] text-muted-foreground">{label}</div>
        <div className="text-sm font-semibold">{value || '0'}</div>
        {badge && <Badge variant="outline" className="text-[9px] mt-0.5">{badge}</Badge>}
      </CardContent>
    </Card>
  );
}
